import { SagaIterator } from "redux-saga";
import { call, delay, put, takeEvery, takeLatest } from "redux-saga/effects";
import { PayloadAction } from "typesafe-actions";

import { message } from "antd";

import { callDelete, callGet } from "../../http_client";
import { openInNewTab } from "../../utils/browser";

import {
  reportGeneratorCancelFetchRequest,
  reportGeneratorIsCompleted,
  reportGeneratorStartError,
  reportGeneratorStartSuccess,
  reportGeneratorStatusFetchError,
  reportGeneratorStatusFetchRequest,
  reportGeneratorStatusFetchSuccess,
  reportsFetchError,
  reportsFetchRequest,
  reportsFetchSuccess,
} from "./actions";
import { ReportGeneratorState, ReportGeneratorStatus, ReportsActionTypes } from "./types";

export function* handleReportsFetch(action: ReturnType<typeof reportsFetchRequest>): SagaIterator {
  try {
    const res = yield call(callGet, "/reports/", action.payload);
    yield put(reportsFetchSuccess(res.data));
  } catch (err) {
    yield put(reportsFetchError(err.message));
  }
}

export function createGenerateReportHandler<T extends PayloadAction<any, any>>(url: string) {
  return function* handleStartReportGenerator(action: T): SagaIterator {
    try {
      const { data } = yield call(callGet, url, action.payload);
      yield put(reportGeneratorStartSuccess(data));
      yield put(reportGeneratorStatusFetchRequest(data));
    } catch (err) {
      yield put(reportGeneratorStartError(err.message));
    }
  };
}

function* handleGeneratorStatusFetchRequest(
  action: ReturnType<typeof reportGeneratorStatusFetchRequest>
): SagaIterator {
  try {
    const { id } = action.payload;
    const { data }: { data: ReportGeneratorState } = yield call(callGet, `/reports/generators/${id}`);
    yield put(reportGeneratorStatusFetchSuccess(data));

    switch (data.status) {
      case ReportGeneratorStatus.PENDING:
      case ReportGeneratorStatus.IN_PROGRESS:
        yield delay(1000);
        yield put(reportGeneratorStatusFetchRequest(data));
        return;
      case ReportGeneratorStatus.DONE:
        yield call(handleGeneratorResultFetchRequest, id);
        yield delay(2000);
        yield put(reportGeneratorIsCompleted(action.payload));
        return;
      default:
        yield delay(2000);
        yield put(reportGeneratorIsCompleted(action.payload));
        return;
    }
  } catch (err) {
    yield put(reportGeneratorStatusFetchError(err.message));
  }
}

function* handleGeneratorResultFetchRequest(generatorId: string): SagaIterator {
  try {
    const { data } = yield call(callGet, `/reports/generators/${generatorId}/result`);
    if (data.url) {
      openInNewTab(data.url);
    }
  } catch (err) {
    yield call(message.error, err.message);
  }
}

function* handleCancelReportGenerator(action: ReturnType<typeof reportGeneratorCancelFetchRequest>): SagaIterator {
  try {
    const { id } = action.payload;
    yield call(callDelete, `/reports/generators/${id}`);
  } catch (err) {
    yield put(reportGeneratorStatusFetchError(err.message));
  }
}

function* watchReportsSagas(): SagaIterator {
  yield takeLatest(ReportsActionTypes.FETCH_REQUEST, handleReportsFetch);
  yield takeEvery(ReportsActionTypes.GENERATOR_STATUS_FETCH_REQUEST, handleGeneratorStatusFetchRequest);
  yield takeEvery(ReportsActionTypes.GENERATOR_CANCEL_FETCH_REQUEST, handleCancelReportGenerator);
}

export default watchReportsSagas;
